import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';

interface ClientFormActionsProps {
  isEditing: boolean;
  isNew: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  isSaving: boolean;
  isDeleting: boolean;
  title: string;
}

export const ClientFormActions: React.FC<ClientFormActionsProps> = ({
  isEditing,
  isNew,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  isSaving,
  isDeleting,
  title,
}) => {
  const showDeleteButton = !isNew && onDelete;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleConfirmDelete = () => {
    if (onDelete) {
      onDelete();
    }
    setIsPopoverOpen(false);
  };

  return (
    <div className="bg-muted/20 border-b rounded-t-lg flex items-center justify-between p-1.5">
      <div>
        {title && (
          <h3 className="text-xs font-medium text-gray-900 px-1">{title}</h3>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        {isEditing ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              disabled={isSaving}
              className="text-muted-foreground/70 hover:text-foreground px-1.5 py-0.5 "
            >
              <span className="text-[10px]">Cancel</span>
            </Button>
            <Separator orientation="vertical" className="h-4 opacity-50" />
            <Button
              variant="ghost"
              size="sm"
              onClick={onSave}
              disabled={isSaving}
              className="text-primary/80 hover:text-primary hover:bg-primary/10 px-1.5 py-0.5 "
            >
              <span className="text-[10px]">
                {isSaving ? 'Saving...' : 'Save'}
              </span>
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              className="text-muted-foreground/70 hover:text-foreground px-1.5 py-0.5 "
            >
              <span className="text-[10px]">Edit</span>
            </Button>
            {showDeleteButton && (
              <>
                <Separator orientation="vertical" className="h-4 opacity-50" />
                <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={isDeleting}
                      className="text-destructive/70 hover:text-destructive hover:bg-destructive/10 px-1.5 py-0.5 "
                    >
                      <span className="text-[10px]">
                        {isDeleting ? 'Deleting...' : 'Delete'}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-2" align="end">
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-gray-900">
                        Delete this client information?
                      </p>
                      <p className="text-[10px] text-gray-500">
                        This action cannot be undone.
                      </p>
                      <div className="flex justify-end gap-1.5 pt-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setIsPopoverOpen(false)}
                          className="h-6 px-2 text-xs"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={handleConfirmDelete}
                          disabled={isDeleting}
                          className="h-6 px-2 text-xs text-white"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
